<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <DrawerToolbar title="Customize Page" :admin="true" @close="handleCancel" />

      <v-card-text class="pa-4">
        <v-form v-model="form.valid">
          <!-- Intro text -->
          <p class="body-1 mb-6">
            Define global styles for buttons, text, and cards. These settings override existing
            component styles and become the default for all new content.
          </p>
          <p class="caption mb-6">
            Default colors meet WCAG accessibility standards. Ensure any custom colors maintain
            4.5:1 contrast.
          </p>

          <!-- Section 1: Button Styling -->
          <div class="section-title mb-4">Button Styling</div>

          <div class="d-flex align-center mb-4">
            <input type="color" v-model="buttonBackgroundColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="buttonBackgroundColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Button background color (in hexadecimal)"
              hide-details="auto"
            />
          </div>

          <div class="d-flex align-center mb-6">
            <input type="color" v-model="buttonTextColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="buttonTextColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Button text color (in hexadecimal)"
              hide-details="auto"
            />
          </div>

          <!-- Section 2: Text Styling -->
          <div class="section-title mb-4">Text Styling</div>

          <div class="d-flex align-center mb-6">
            <input type="color" v-model="textColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="textColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Text color (in hexadecimal)"
              hide-details="auto"
            />
          </div>

          <!-- Section 3: Card Background Styling -->
          <div class="section-title mb-4">Card Background Styling</div>

          <div class="d-flex align-center mb-4">
            <input type="color" v-model="cardBackgroundColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="cardBackgroundColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Primary color (in hexadecimal)"
              hide-details="auto"
            />
          </div>

          <div v-if="showGradient" class="d-flex align-center mb-6">
            <input type="color" v-model="gradientColor" class="color-swatch mr-2" />
            <v-text-field
              v-model="gradientColor"
              dense
              outlined
              :rules="hexColorRules"
              label="Gradient secondary color (in hexadecimal)"
              hide-details="auto"
            />
            <v-btn icon class="ml-2" @click="removeGradient">
              <v-icon color="grey darken-1">mdi-delete</v-icon>
            </v-btn>
          </div>

          <div v-else class="mb-6">
            <v-btn text small color="brandPrimary" @click="addGradient">
              <v-icon small class="mr-1">mdi-plus</v-icon>
              ADD GRADIENT
            </v-btn>
          </div>

          <Alert
            variant="warning"
            message="Warning: Saving applies these styles globally, overwriting all existing component settings."
            class="mx-4"
            style="background-color: #fff4e5 !important"
          />

          <div v-if="error.general" class="mt-2">
            <p class="error--text body-2">{{ error.general }}</p>
          </div>
        </v-form>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn text color="brandPrimary" @click="handleCancel">Cancel</v-btn>
        <v-btn
          color="brandPrimary"
          class="white--text"
          rounded
          depressed
          :loading="loading.saving"
          @click="handleSave"
        >
          Save
        </v-btn>
      </v-card-actions>

      <!-- Cancel confirmation dialog -->
      <UnsavedChangesDialog
        v-model="dialog.confirmCancel"
        body-text="Unsaved Changes. Are you sure you want to cancel? All changes will be lost."
        stay-label="Stay"
        discard-label="Discard Changes"
        @stay="dialog.confirmCancel = false"
        @discard="handleCancelConfirmed"
      />
    </v-col>
  </v-row>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Alert from "@/components/generic/Alert.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import ProgramService from "@/services/ProgramService";
import { mapState } from "vuex";

function initialState() {
  return {
    buttonBackgroundColor: "#077DAF",
    buttonTextColor: "#FFFFFF",
    textColor: "#000000",
    cardBackgroundColor: "#FFFFFF",
    showGradient: false,
    gradientColor: "#FFFFFF",
    hexColorRules: [
      (v) => !!v || "Color is required",
      (v) => !v || v.startsWith("#") || "Hex color value should start with #",
      (v) => !v || v.length === 7 || "Hex color value should be 6 characters",
      (v) => !v || /^#[0-9A-Fa-f]{6}$/.test(v) || "Invalid hex color value",
    ],
    form: {
      valid: true,
    },
    dialog: {
      confirmCancel: false,
    },
    loading: {
      saving: false,
    },
    error: {
      general: null,
    },
    originalColors: null,
    loadingColors: false,
  };
}

export default {
  name: "DashboardCustomizeSideSheet",
  components: { DrawerToolbar, Alert, UnsavedChangesDialog },
  data() {
    return initialState();
  },
  computed: {
    ...mapState(["globalClientId"]),
    clientId() {
      return this.globalClientId;
    },
    isDirty() {
      if (!this.originalColors) return false;
      return (
        this.buttonBackgroundColor !== this.originalColors.buttonBackgroundColor ||
        this.buttonTextColor !== this.originalColors.buttonTextColor ||
        this.textColor !== this.originalColors.textColor ||
        this.cardBackgroundColor !== this.originalColors.cardBackgroundColor ||
        this.showGradient !== this.originalColors.showGradient ||
        this.gradientColor !== this.originalColors.gradientColor
      );
    },
  },

  async created() {
    await this.loadSavedColors();
  },

  methods: {
    async loadSavedColors() {
      this.loadingColors = true;
      try {
        const response = await ProgramService.getClientDashboardDefaults(this.clientId);
        const saved = response?.data?.result;
        if (saved && Object.keys(saved).length > 0) {
          this.buttonBackgroundColor = saved.buttonBackgroundColor || this.buttonBackgroundColor;
          this.buttonTextColor = saved.buttonTextColor || this.buttonTextColor;
          this.textColor = saved.textColor || this.textColor;
          this.cardBackgroundColor = saved.cardBackgroundColor || this.cardBackgroundColor;
          if (saved.cardBackgroundGradient) {
            this.showGradient = true;
            this.gradientColor = saved.cardBackgroundGradient;
          } else {
            this.showGradient = false;
          }
        }
        this.originalColors = {
          buttonBackgroundColor: this.buttonBackgroundColor,
          buttonTextColor: this.buttonTextColor,
          textColor: this.textColor,
          cardBackgroundColor: this.cardBackgroundColor,
          showGradient: this.showGradient,
          gradientColor: this.gradientColor,
        };
      } catch (e) {
        console.error("Error loading dashboard colors:", e);
        this.originalColors = {
          buttonBackgroundColor: this.buttonBackgroundColor,
          buttonTextColor: this.buttonTextColor,
          textColor: this.textColor,
          cardBackgroundColor: this.cardBackgroundColor,
          showGradient: this.showGradient,
          gradientColor: this.gradientColor,
        };
      } finally {
        this.loadingColors = false;
      }
    },
    close() {
      this.$emit("close");
    },
    handleCancel() {
      if (this.isDirty) {
        this.dialog.confirmCancel = true;
      } else {
        this.close();
      }
    },
    handleCancelConfirmed() {
      this.dialog.confirmCancel = false;
      this.close();
    },
    async handleSave() {
      if (!this.form.valid || this.loading.saving) return;
      this.loading.saving = true;
      this.error.general = null;
      try {
        const payload = {
          buttonBackgroundColor: this.buttonBackgroundColor,
          buttonTextColor: this.buttonTextColor,
          textColor: this.textColor,
          cardBackgroundColor: this.cardBackgroundColor,
          cardBackgroundGradient: this.showGradient ? this.gradientColor : null,
        };
        await ProgramService.updateClientDashboardDefaults(this.clientId, payload);
        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        console.error("Error saving global customization", e);
        this.error.general =
          e?.response?.data?.error || e.message || "Failed to save. Please try again.";
      } finally {
        this.loading.saving = false;
      }
    },
    addGradient() {
      this.showGradient = true;
    },
    removeGradient() {
      this.showGradient = false;
      this.gradientColor = "#FFFFFF";
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 16px;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.87);
}

.color-swatch {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
}
</style>
